import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Users, User, Bell, Settings, Cookie, Brain } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/context/AuthContext';
import { isAgencyOwnerRole, isAdminRole } from '@/lib/roles';
import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import {
  COOKIEBOT_LOAD_EVENT,
  isCookiebotAvailable,
  openCookiePreferences,
} from '@/lib/cookiebot';

interface SettingsSection {
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  visible: boolean;
}

export default function SettingsPage() {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { isEnabled } = useResolvedFeatureFlags();

  // Cookiebot loads asynchronously, so the card only appears once its API is ready.
  // Subscribe before re-checking: CookiebotOnLoad can fire between the initial render and
  // this effect, and a listener installed after that point would never see it.
  const [cookiebotReady, setCookiebotReady] = useState(isCookiebotAvailable);
  useEffect(() => {
    const handleLoad = () => setCookiebotReady(isCookiebotAvailable());
    window.addEventListener(COOKIEBOT_LOAD_EVENT, handleLoad);
    handleLoad();
    return () => window.removeEventListener(COOKIEBOT_LOAD_EVENT, handleLoad);
  }, []);

  // AR-278 — members can reset what AIVIA has learned about them (knowledge
  // graph + cached chat summaries). Their profile, threads and messages stay.
  const [confirmClearMemory, setConfirmClearMemory] = useState(false);
  const clearMemory = useMutation({
    mutationFn: () => apiRequest('DELETE', '/api/knowledge-graph/me/memory'),
    onSuccess: () => {
      setConfirmClearMemory(false);
      toast({
        title: 'AIVIA memory cleared',
        description: 'AIVIA will start learning about you again from your next conversation.',
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Could not clear AIVIA memory',
        description: error instanceof Error ? error.message : 'Please try again in a moment.',
        variant: 'destructive',
      });
    },
  });

  const sections: SettingsSection[] = [
    {
      title: 'Profile',
      description: 'Manage your personal information and account details.',
      icon: <User className="h-6 w-6" />,
      action: () => navigate('/profile'),
      visible: true,
    },
    {
      title: 'Preferences',
      description: 'Customize your experience and display options.',
      icon: <Settings className="h-6 w-6" />,
      action: () => navigate('/user-preferences'),
      visible: true,
    },
    {
      title: 'Team Management',
      description: 'Manage your agency team members and assign roles.',
      icon: <Users className="h-6 w-6" />,
      action: () => navigate('/settings/team'),
      visible: (isAgencyOwnerRole(user?.role) || isAdminRole(user?.role)) && isEnabled('invoicingMvp'),
    },
    {
      title: 'AIVIA Memory',
      description: 'Clear what AIVIA has learned about you from past conversations and start fresh.',
      icon: <Brain className="h-6 w-6" />,
      action: () => setConfirmClearMemory(true),
      visible: true,
    },
    {
      title: 'Cookie Preferences',
      description: 'Review or change which cookies AIVIA may use on this device.',
      icon: <Cookie className="h-6 w-6" />,
      action: openCookiePreferences,
      visible: cookiebotReady,
    },
    {
      title: 'Notifications',
      description: 'Configure how and when you receive notifications.',
      icon: <Bell className="h-6 w-6" />,
      action: () => navigate('/settings'),
      visible: false,
    },
  ];

  const visibleSections = sections.filter((s) => s.visible);

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your account, preferences, and team.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {visibleSections.map((section) => (
          <Card
            key={section.title}
            className="cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            role="link"
            tabIndex={0}
            onClick={section.action}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                section.action();
              }
            }}
          >
            <CardHeader className="flex flex-row items-center gap-4 pb-2">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
                {section.icon}
              </div>
              <div>
                <CardTitle className="text-base">{section.title}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>{section.description}</CardDescription>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog
        open={confirmClearMemory}
        onOpenChange={(open) => {
          if (!clearMemory.isPending) setConfirmClearMemory(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear AIVIA memory?</AlertDialogTitle>
            <AlertDialogDescription>
              AIVIA will forget the interests, topics and details it has picked up from your past
              conversations. Your profile, threads and messages are not deleted. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearMemory.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={clearMemory.isPending}
              onClick={(e) => {
                // Keep the dialog open until the server answers.
                e.preventDefault();
                clearMemory.mutate();
              }}
            >
              {clearMemory.isPending ? 'Clearing…' : 'Clear memory'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
